import React, { useState, useRef, useMemo, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Play, ExternalLink, Eye } from 'lucide-react';
import { getFallbackDayInLifeVideos } from '../utils/dayInLifeVideos';
import './VideoCarousel.css';

const VideoCarousel = ({ videos = [], loading = false, roleTitle = 'Career' }) => {
  const scrollRef = useRef(null);
  const [thumbnails, setThumbnails] = useState({});

  const preparedVideos = useMemo(() => {
    const fallback = getFallbackDayInLifeVideos(roleTitle);
    if (videos && videos.length > 0) return videos;
    return fallback;
  }, [videos, roleTitle]);

  useEffect(() => {
    const fetchSocialThumbnails = async () => {
      // Direct oEmbed calls are disabled due to CORS/Auth issues.
      // We now rely on deterministic thumbnail generation or backend-provided thumbnails.
    };

    // fetchSocialThumbnails();
  }, [preparedVideos]);

  const getYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const getThumbnailUrl = (video) => {
    if (thumbnails[video.url]) return thumbnails[video.url];

    // 1. Use API-provided thumbnail (YouTube API provides high-quality thumbnails)
    if (video.thumbnail) {
      return video.thumbnail;
    }

    // 2. Fallback: Try deterministic YouTube thumbnail
    if (video.platform === 'YouTube' || video.url.includes('youtube') || video.url.includes('youtu.be')) {
      const videoId = getYouTubeId(video.url);
      if (videoId) {
        return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
      }
    }

    return null;
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const { current } = scrollRef;
      const scrollAmount = 300;
      if (direction === 'left') {
        current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      } else {
        current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    }
  };

  return (
    <div className="video-carousel-container">
      <div className="carousel-header-container">
        <h3 className="text-lg font-semibold text-slate-900">Day in the Life & Insights</h3>
        <div className="carousel-controls">
          <button onClick={() => scroll('left')} className="control-btn">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={() => scroll('right')} className="control-btn">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      <div className="carousel-track" ref={scrollRef}>
        {loading ? (
          // Skeleton Loading State
          Array(4).fill(0).map((_, index) => (
            <div key={`skeleton-${index}`} className="skeleton-card">
              <div className="skeleton-thumb skeleton"></div>
              <div className="skeleton-info">
                <div className="skeleton-title skeleton"></div>
                <div className="skeleton-title-short skeleton"></div>
                <div className="skeleton-author skeleton"></div>
              </div>
            </div>
          ))
        ) : (
          // Real Video Cards
          preparedVideos.map((video, index) => {
            const thumbnailUrl = getThumbnailUrl(video);
            // Platform-specific gradient colors
            const platformGradients = {
              'TikTok': 'linear-gradient(135deg, #010101 0%, #69C9D0 50%, #EE1D52 100%)',
              'Instagram': 'linear-gradient(135deg, #405DE6 0%, #C13584 50%, #F56040 100%)',
              'YouTube': 'linear-gradient(135deg, #FF0000 0%, #282828 100%)'
            };
            const placeholderBg = platformGradients[video.platform] || 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)';

            return (
              <a
                key={index}
                href={video.url}
                target="_blank"
                rel="noopener noreferrer"
                className="video-card"
              >
                <div className="video-thumbnail">
                  {thumbnailUrl ? (
                    <img src={thumbnailUrl} alt={video.title} onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }} />
                  ) : null}
                  <div className="placeholder-thumb" style={{
                    background: placeholderBg,
                    display: thumbnailUrl ? 'none' : 'flex'
                  }}>
                    <Play className="w-8 h-8 text-white opacity-80" />
                  </div>
                  <span className={`platform-badge platform-${video.platform?.toLowerCase()}`}>{video.platform}</span>
                  {video.viewCountFormatted && (
                    <span className="view-count-badge">
                      <Eye className="w-3 h-3" />
                      {video.viewCountFormatted}
                    </span>
                  )}
                </div>
                <div className="video-info">
                  <h4 className="video-title" title={video.title}>{video.title}</h4>
                  <div className="video-author">
                    <span>{video.creator || video.author || video.platform}</span>
                    <ExternalLink className="w-3 h-3 ml-1 opacity-50" />
                  </div>
                </div>
              </a>
            );
          })
        )}
      </div>
    </div>
  );
};

export default VideoCarousel;
